import { BaseModel } from '@app/core/service/base-model';
import { CLINICIAN_ROLES, USER_ROLE } from '@app/modules/user/consts/role';
import { IUser, Patient } from '@app/modules/user/interface/user';
import { FormConfigItem } from '@app/shared/interfaces/form-config-item';
import { ModelAttributes } from '@app/core/interface/common';
import { US_STATES } from '@app/shared/consts/us-states';

export class UserModel extends BaseModel<IUser> {
  protected populateAttributes(): void {
    this.attributes = {
      id: null,
      cognitoId: null,
      patients: null,
      activePatient: null,
      token: null,
      role: null,
      email: null,
      inviterId: null,
      invitationId: null,
      firstName: null,
      lastName: null,
      address: null,
      avatar: 'assets/icon/default-user-avatar.svg',
      city: null,
      dateOfBirth: null,
      phoneNumber: null,
      relationship: null,
      state: null,
      zip: null,
      ssn: null,
      isWelcomeMessageViewed: true,
      hasPendingInvitations: false,
    };
  }

  public get id(): string {
    return this.attributes.id;
  }

  public get token(): string {
    return this.attributes.token;
  }

  public get email(): string {
    return this.attributes.email;
  }

  public get firstName(): string {
    return this.attributes.firstName;
  }

  public get lastName(): string {
    return this.attributes.lastName;
  }

  public get avatar(): string {
    return this.attributes.avatar;
  }

  public get dateOfBirth(): Date {
    const timestamp = this.attributes.dateOfBirth;
    return timestamp ? new Date(+timestamp) : timestamp;
  }

  public get patients(): ModelAttributes[] {
    return this.attributes.patients;
  }

  public get activePatient(): Patient {
    return this.attributes.activePatient;
  }

  public get ssn(): string {
    return this.attributes.ssn;
  }

  public get address(): string {
    return this.attributes.address;
  }

  public get phoneNumber(): string {
    return this.attributes.phoneNumber;
  }

  public get state(): string {
    return this.attributes.state;
  }

  public get city(): string {
    return this.attributes.city;
  }

  public get zip(): string {
    return this.attributes.zip;
  }

  public get fullName(): string {
    return `${this.firstName || ''} ${this.lastName || ''}`.trim();
  }

  public get role(): USER_ROLE {
    return this.attributes.role;
  }

  public get isWelcomeMessageViewed(): boolean {
    return this.attributes.isWelcomeMessageViewed;
  }

  public get hasPendingInvitations(): boolean {
    return this.attributes.hasPendingInvitations;
  }

  public get isPatient(): boolean {
    return this.role === USER_ROLE.patient;
  }

  // "Doctor" in the UI sense = any order-signing clinician (MD / NP / PA);
  // they share the same screens and flows.
  public get isDoctor(): boolean {
    return CLINICIAN_ROLES.includes(this.role as USER_ROLE);
  }

  public get isAdmin(): boolean {
    return this.role === USER_ROLE.admin;
  }

  public get isProxy(): boolean {
    return this.role === USER_ROLE.proxy;
  }

  public get personalInfoRules(): FormConfigItem[] {
    let result = [];

    const firstName = (width: number) => {
      return Object.freeze({
        width,
        label: 'First name',
        placeholder: 'John',
        value: this.firstName,
        type: 'string',
        name: 'firstName',
        validators: [
          { name: 'pattern', value: '[a-zA-Z ]*' },
          { name: 'minLength', value: 2 },
        ],
      });
    };
    const lastName = (width: number) => {
      return Object.freeze({
        width,
        label: 'Last name',
        placeholder: 'Doe',
        value: this.lastName,
        type: 'string',
        name: 'lastName',
        validators: [
          { name: 'pattern', value: '[a-zA-Z ]*' },
          { name: 'minLength', value: 2 },
        ],
      });
    };
    const dateOfBirth = (width: number) => {
      return Object.freeze({
        width,
        label: 'Date of birth',
        placeholder: '06-08-1991',
        value: this.dateOfBirth,
        type: 'calendar',
        name: 'dateOfBirth',
      });
    };
    const ssn = (width) => {
      return Object.freeze({
        width,
        label: 'SSN',
        placeholder: '999-99-9999',
        value: this.ssn,
        type: 'mask',
        name: 'ssn',
        mask: '999-99-9999',
      });
    };

    switch (true) {
      case this.isPatient:
        result = [firstName(5), lastName(7), dateOfBirth(5), ssn(7)];
        break;
      case this.isProxy:
        result = [firstName(5), lastName(7), dateOfBirth(5), ssn(7)];
        break;
      case this.isDoctor:
        result = [firstName(5), lastName(7)];
        break;
      default:
        result = [firstName(5), lastName(7), dateOfBirth(5), ssn(7)];
    }

    return result;
  }

  public get contactInfoRules(): FormConfigItem[] {
    let result = [];

    const address = (width: number) => {
      return Object.freeze({
        width,
        label: 'Address',
        placeholder: '345-4567 Burrard St',
        type: 'string',
        name: 'address',
        value: this.address,
        validators: [{ name: 'minLength', value: 3 }],
      });
    };
    const phoneNumber = (width: number, label: string) => {
      return Object.freeze({
        width,
        label,
        value: this.phoneNumber,
        placeholder: '+1 (999) 999 9999',
        type: 'mask',
        name: 'phoneNumber',
        mask: '+1 (999) 999 9999',
        validators: [{ name: 'minLength', value: 10 }],
      });
    };
    const state = (width: number) => {
      return Object.freeze({
        width,
        label: 'State',
        value: this.state,
        placeholder: 'Select a state',
        type: 'dropdown',
        name: 'state',
        list: US_STATES,
      });
    };
    const city = (width: number) => {
      return Object.freeze({
        width,
        label: 'City',
        value: this.city,
        placeholder: 'Seattle',
        type: 'string',
        name: 'city',
        validators: [
          { name: 'pattern', value: '[a-zA-Z ]*' },
          { name: 'minLength', value: 3 },
        ],
      });
    };
    const zip = (width: number) => {
      return Object.freeze({
        width,
        label: 'ZIP',
        value: this.zip,
        placeholder: '99999',
        type: 'mask',
        name: 'zip',
        mask: '99999',
      });
    };

    switch (true) {
      case this.isPatient:
        result = [
          address(7),
          phoneNumber(5, 'Contact phone'),
          state(4),
          city(4),
          zip(4),
        ];
        break;
      case this.isProxy:
        result = [
          phoneNumber(5, 'Home phone'),
          address(7),
          state(4),
          city(4),
          zip(4),
        ];
        break;
      case this.isDoctor:
        result = [
          phoneNumber(5, 'Contact phone'),
          address(7),
          state(4),
          city(4),
          zip(4),
        ];
        break;
      default:
        result = [
          address(7),
          phoneNumber(5, 'Contact phone'),
          state(4),
          city(4),
          zip(4),
        ];
    }
    return result;
  }
}
